import React, { useEffect, useState } from "react";

const USE_MOCK_API = false;

const mockApiData = [
    {
        name: "Ethereum Mainnet",
        endpoint: "https://mainnet.infura.io/v3/YOUR-PROJECT-ID",
        wsendpoint: "https://mainnet.infura.io/v3/YOUR-PROJECT-ID",
        status: "Online",
        latency: "120ms",
        showkeys: "false",
        archive: "true",
    },
    {
        name: "Polygon",
        endpoint: "https://polygon-rpc.com",
        wsendpoint: "https://polygon-rpc.com",
        status: "Online",
        latency: "80ms",
        showkeys: "false",
        archive: "true",
    },
    {
        name: "Arbitrum",
        endpoint: "https://arb1.arbitrum.io/rpc",
        wsendpoint: "https://arb1.arbitrum.io/rpc",
        status: "Offline",
        latency: "-",
        showkeys: "true",
        archive: "true",
    },
    {
        name: "Binance Smart Chain",
        endpoint: "https://bsc-dataseed.binance.org/",
        wsendpoint: "https://bsc-dataseed.binance.org/",
        status: "Online",
        latency: "100ms",
        showkeys: "false",
        archive: "false",
    },
];

async function fetchRpcNodes() {
    if (USE_MOCK_API) {
        return new Promise((resolve) => {
            setTimeout(() => resolve(mockApiData), 500);
        });
    } else {
        const response = await fetch("/routes");
        if (!response.ok) throw new Error("Failed to fetch RPC routes");
        return response.json();
    }
}

function StatusDot({ status }) {
    const color =
        status === "Online"
            ? "bg-lp-grass"
            : status === "Degraded"
                ? "bg-yellow-500"
                : "bg-red-500";
    return (
        <span className="flex items-center gap-2">
            <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
            <span
                className={
                    status === "Online"
                        ? "text-lp-grass"
                        : status === "Degraded"
                            ? "text-yellow-500"
                            : "text-red-400"
                }
            >
                {status}
            </span>
        </span>
    );
}

export default function RpcNodeTable() {
    const [rpcNodes, setRpcNodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [copiedKey, setCopiedKey] = useState(null);
    const [expandedIdx, setExpandedIdx] = useState(null);

    const formatImage = (image) => {
        if (!image) return image;
        const prefix = "ghcr.io/linkpoolio/docker-images/";
        if (image.startsWith(prefix)) {
            const tagIdx = image.indexOf(":", prefix.length);
            const tag = tagIdx >= 0 ? image.slice(tagIdx + 1) : "";
            return tag ? `internal-build - ${tag}` : "internal-build";
        }
        return image;
    };

    const copyToClipboard = async (text, key) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); } catch {}
            document.body.removeChild(ta);
        }
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    };

    useEffect(() => {
        fetchRpcNodes()
            .then((data) => {
                setRpcNodes(data);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    const availableStatuses = Array.from(
        new Set(rpcNodes.map((n) => n.status).filter(Boolean))
    ).sort();

    const filteredNodes = rpcNodes.filter((node) => {
        const matchesSearch =
            node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            node.endpoint.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    // Collapse multiple HTTPRoute rows that belong to the same chain (same `app`)
    // into one grouped row. Per-replica URLs and statuses go into the expanded panel.
    const groupedNodes = (() => {
        const order = [];
        const groups = new Map();
        for (const r of filteredNodes) {
            const key = r.app || r.name;
            if (!groups.has(key)) {
                groups.set(key, []);
                order.push(key);
            }
            groups.get(key).push(r);
        }
        return order.map((key) => {
            const replicas = groups.get(key);
            const primary =
                replicas.find((r) => !/\s\[[^\]]+\]$/.test(r.name)) || replicas[0];
            return { key, primary, replicas };
        }).filter((g) =>
            statusFilter === "All" ||
            g.replicas.some((r) => r.status === statusFilter)
        );
    })();

    // Multiple HTTPRoutes can point at the same fullnode (e.g. -gw-cll, -gw-cll-old)
    // so the stats counter dedupes by `app` (falling back to name when missing).
    const uniqueNodes = (() => {
        const seen = new Map();
        for (const n of rpcNodes) {
            const key = n.app || n.name;
            if (!seen.has(key)) seen.set(key, n);
        }
        return Array.from(seen.values());
    })();
    const totalNodes = uniqueNodes.length;
    const onlineCount = uniqueNodes.filter((n) => n.status === "Online").length;

    return (
        <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Stats bar */}
            <div className="flex items-center gap-6 mb-6">
                <div>
                    <p className="text-lp-light-green/60 text-xs font-medium uppercase tracking-widest">
                        Total Nodes
                    </p>
                    <p className="text-lp-lime text-3xl font-bold">{totalNodes}</p>
                </div>
                <div className="h-10 w-px bg-lp-grass/20" />
                <div>
                    <p className="text-lp-light-green/60 text-xs font-medium uppercase tracking-widest">
                        Online
                    </p>
                    <p className="text-lp-grass text-3xl font-bold">{onlineCount}</p>
                </div>
                <div className="h-10 w-px bg-lp-grass/20" />
                <div>
                    <p className="text-lp-light-green/60 text-xs font-medium uppercase tracking-widest">
                        Offline
                    </p>
                    <p className="text-red-400 text-3xl font-bold">
                        {totalNodes - onlineCount}
                    </p>
                </div>
            </div>

            {/* Search + Status filter */}
            <div className="mb-6 flex flex-wrap items-center gap-3">
                <input
                    type="text"
                    placeholder="Search by name or endpoint..."
                    aria-label="Search nodes by name or endpoint"
                    className="px-4 py-2.5 bg-lp-space border border-lp-grass/30 text-lp-lime placeholder-lp-light-green/40 w-full max-w-md focus:outline-none focus:border-lp-grass transition-colors"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                    aria-label="Filter by status"
                    className="px-3 py-2.5 bg-lp-space border border-lp-grass/30 text-lp-lime focus:outline-none focus:border-lp-grass transition-colors"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="All">All statuses</option>
                    {availableStatuses.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center gap-3 text-lp-light-green/60 py-12">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                    </svg>
                    Loading nodes...
                </div>
            ) : error ? (
                <p className="text-red-400 py-8">Error: {error}</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead>
                            <tr className="border-b border-lp-grass/20">
                                <th className="py-3 px-2 w-8" aria-label="Expand"></th>
                                <th className="py-3 px-4 text-left text-xs font-semibold text-lp-light-green/60 uppercase tracking-wider">
                                    Node
                                </th>
                                <th className="py-3 px-4 text-left text-xs font-semibold text-lp-light-green/60 uppercase tracking-wider">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedNodes.map((group, idx) => {
                              const isExpanded = expandedIdx === idx;
                              const node = group.primary;
                              const replicas = group.replicas;
                              const replicaLabel = (r) => {
                                  const m = r.name.match(/\s\[([^\]]+)\]$/);
                                  if (m) return m[1];
                                  return replicas.length > 1 ? "root" : "default";
                              };
                              const displayedVersions = (node.versions || []).filter(
                                  (v) => v.name !== "healthcheck"
                              );
                              return (
                                <React.Fragment key={group.key}>
                                <tr
                                    onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                                    className={`border-b border-lp-grass/10 transition-colors cursor-pointer hover:bg-lp-grass/10 ${
                                        isExpanded ? "bg-lp-grass/5" : ""
                                    }`}
                                >
                                    <td className="py-3.5 px-2 text-center align-middle w-8">
                                        <span
                                            aria-hidden="true"
                                            className={`inline-block text-lp-grass transition-transform duration-150 ${
                                                isExpanded ? "rotate-90" : ""
                                            }`}
                                        >▶</span>
                                    </td>
                                    <td className="py-3.5 px-4 flex items-center gap-2.5">
                                        {node.logo && (
                                            <img
                                                src={node.logo}
                                                alt={`${node.name} logo`}
                                                className="w-5 h-5 rounded-full"
                                            />
                                        )}
                                        <span className="text-lp-lime font-medium">
                                            {node.name}
                                        </span>
                                        {replicas.length > 1 && (
                                            <span className="inline-block bg-lp-grass/10 text-lp-light-green/80 text-[10px] font-mono px-1.5 py-0.5">
                                                ×{replicas.length}
                                            </span>
                                        )}
                                        {node.archive === "true" && (
                                            <span className="inline-block bg-lp-grass/15 text-lp-grass text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
                                                Archive
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-3.5 px-4">
                                        <StatusDot status={node.status} />
                                    </td>
                                </tr>
                                {isExpanded && (
                                    <tr className="bg-lp-space/40 border-b border-lp-grass/10">
                                        <td></td>
                                        <td colSpan={2} className="py-4 px-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <h4 className="text-lp-light-green/60 text-[10px] uppercase tracking-widest mb-2">
                                                        Metadata
                                                    </h4>
                                                    <dl className="text-sm text-lp-light-green/90 space-y-1 font-mono">
                                                        {node.app && (
                                                            <div className="flex gap-2">
                                                                <dt className="text-lp-light-green/50 w-24 shrink-0">app</dt>
                                                                <dd>{node.app}</dd>
                                                            </div>
                                                        )}
                                                        {node.namespace && (
                                                            <div className="flex gap-2">
                                                                <dt className="text-lp-light-green/50 w-24 shrink-0">namespace</dt>
                                                                <dd>{node.namespace}</dd>
                                                            </div>
                                                        )}
                                                        <div className="flex gap-2">
                                                            <dt className="text-lp-light-green/50 w-24 shrink-0">nodes</dt>
                                                            <dd>{replicas.length}</dd>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <dt className="text-lp-light-green/50 w-24 shrink-0">archive</dt>
                                                            <dd>{node.archive === "true" ? "yes" : "no"}</dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                                {displayedVersions.length > 0 && (
                                                    <div>
                                                        <h4 className="text-lp-light-green/60 text-[10px] uppercase tracking-widest mb-2">
                                                            Container versions
                                                        </h4>
                                                        <table className="text-sm font-mono w-full">
                                                            <tbody>
                                                                {displayedVersions.map((v) => (
                                                                    <tr key={v.name} className="align-top">
                                                                        <td className="text-lp-light-green/50 pr-3 py-0.5 whitespace-nowrap">{v.name}</td>
                                                                        <td className="text-lp-light-green/90 py-0.5 break-all">{formatImage(v.image)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                                <div className="md:col-span-2">
                                                    <h4 className="text-lp-light-green/60 text-[10px] uppercase tracking-widest mb-2">
                                                        Endpoints ({replicas.length})
                                                    </h4>
                                                    <table className="text-sm font-mono w-full">
                                                        <thead>
                                                            <tr className="text-lp-light-green/40 text-xs">
                                                                <th className="text-left pr-3 py-1 font-normal whitespace-nowrap"></th>
                                                                <th className="text-left pr-3 py-1 font-normal">HTTP</th>
                                                                <th className="text-left pr-3 py-1 font-normal">WSS</th>
                                                                <th className="text-left py-1 font-normal">status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {replicas.map((r, ridx) => {
                                                                const needsApiKey = r.keyed && !r.showkeys;
                                                                const httpUrl =
                                                                    "https://" +
                                                                    r.endpoint +
                                                                    (needsApiKey ? "/?apikey=your-api-key" : "");
                                                                const hasWs = Boolean(r.wsendpoint);
                                                                const wsUrl = hasWs
                                                                    ? "wss://" +
                                                                      r.wsendpoint +
                                                                      (needsApiKey ? "/ws/?apikey=your-api-key" : "")
                                                                    : "";
                                                                const httpKey = `http-${idx}-${ridx}`;
                                                                const wsKey = `ws-${idx}-${ridx}`;
                                                                const httpCopied = copiedKey === httpKey;
                                                                const wsCopied = copiedKey === wsKey;
                                                                return (
                                                                    <tr key={ridx} className="align-top">
                                                                        <td className="text-lp-light-green/50 pr-3 py-1 whitespace-nowrap">
                                                                            {replicaLabel(r)}
                                                                        </td>
                                                                        <td className="pr-3 py-1 break-all">
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => { e.stopPropagation(); copyToClipboard(httpUrl, httpKey); }}
                                                                                title="Click to copy"
                                                                                className={`text-left bg-transparent border-0 p-0 cursor-pointer transition-colors ${
                                                                                    httpCopied ? "text-lp-grass" : "text-lp-light-green/90 hover:text-lp-lime"
                                                                                }`}
                                                                            >
                                                                                {httpCopied ? "Copied!" : httpUrl}
                                                                            </button>
                                                                        </td>
                                                                        <td className="pr-3 py-1 break-all">
                                                                            {hasWs ? (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(wsUrl, wsKey); }}
                                                                                    title="Click to copy"
                                                                                    className={`text-left bg-transparent border-0 p-0 cursor-pointer transition-colors ${
                                                                                        wsCopied ? "text-lp-grass" : "text-lp-light-green/90 hover:text-lp-lime"
                                                                                    }`}
                                                                                >
                                                                                    {wsCopied ? "Copied!" : wsUrl}
                                                                                </button>
                                                                            ) : (
                                                                                <span className="text-lp-light-green/30">—</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="py-1 whitespace-nowrap">
                                                                            <StatusDot status={r.status} />
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {node.snapshots && node.snapshots.length > 0 && (
                                                    <div className="md:col-span-2">
                                                        <h4 className="text-lp-light-green/60 text-[10px] uppercase tracking-widest mb-2">
                                                            Recent snapshots
                                                        </h4>
                                                        <table className="text-sm font-mono w-full">
                                                            <thead>
                                                                <tr className="text-lp-light-green/40 text-xs">
                                                                    <th className="text-left pr-3 py-1 font-normal">name</th>
                                                                    <th className="text-left pr-3 py-1 font-normal">created</th>
                                                                    <th className="text-left py-1 font-normal">phase</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {node.snapshots.map((s) => {
                                                                    const phaseColor =
                                                                        s.phase === "Completed"
                                                                            ? "text-lp-grass"
                                                                            : s.phase === "PartiallyFailed" || s.phase === "Failed"
                                                                                ? "text-yellow-500"
                                                                                : "text-lp-light-green/70";
                                                                    return (
                                                                        <tr key={s.name} className="align-top">
                                                                            <td className="text-lp-light-green/90 pr-3 py-0.5 break-all">{s.name}</td>
                                                                            <td className="text-lp-light-green/70 pr-3 py-0.5 whitespace-nowrap">{s.createdAt || "—"}</td>
                                                                            <td className={`${phaseColor} py-0.5 whitespace-nowrap`}>{s.phase || "—"}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                              );
                            })}
                        </tbody>
                    </table>
                    {groupedNodes.length === 0 && (
                        <p className="text-lp-light-green/40 text-center py-8">
                            No nodes match your search.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
